import { useAppSelector } from 'hooks/store';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';
import { useSlidesCdnCookies } from 'hooks/useSlidesCdnCookies';
import React from 'react';
import { Socket } from 'socket.io-client';
import { selectSlideStatus } from 'store/slice/pluginStatus.slice';

interface HtmlContentProps {
  socket: Socket | undefined;
  htmlLink: string;
  classId: string;
  currentState: string;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  onIframeLoad: () => void;
}

const HtmlContent = ({
  htmlLink,
  classId,
  iframeRef,
  currentState,
  onIframeLoad,
}: HtmlContentProps) => {
  const selectedSlideStatus = useAppSelector(selectSlideStatus);
  const { isStacked, zoom } = useResponsiveLayout();
  const cdnReady = useSlidesCdnCookies();

  // Guard against undefined htmlLink, and hold the deck until the CDN cookies are in.
  if (!htmlLink || !cdnReady) {
    return null;
  }

  const separator = htmlLink.includes('?') ? '&' : '?';
  const link =
    htmlLink +
    separator +
    'classId=' +
    classId +
    '&classType=inClass&userType=student&classPhase=' +
    (currentState ? currentState : 'teach') +
    '&forTM=true';

  // The viewport-relative heights below assume a landscape frame. In the stacked
  // parent layout the slide has to fill whatever height its flex parent grants it,
  // otherwise the 90vh iframe overflows and leaves a large black band.
  //
  // Everywhere else the heights stay in vh but must be divided by `zoom`: the
  // recording root already compensates its own 100vh, and a raw 90vh here would
  // render at 90 * zoom of the visible height, shrinking the slide into the top
  // of the frame while the rest of the container stays black.
  const isShown = Boolean(selectedSlideStatus?.show);
  const shownHeightClass = isStacked ? 'h-full' : '';
  const shownHeightStyle = isShown && !isStacked ? { height: `${100 / zoom}vh` } : undefined;
  const iframeHeight = isShown ? (isStacked ? '100%' : `${90 / zoom}vh`) : '0vh';

  return (
    <div
      style={shownHeightStyle}
      className={`${
        isShown
          ? `${shownHeightClass} grid grid-rows-[auto,1fr] min-h-0 opacity-100`
          : 'h-[0vh] opacity-0'
      }`}
    >
      <div className='pt-2 subHeading'>
        <span className='px-3 text-sm font-medium text-white sm:text-base'>Notes</span>
      </div>
      <iframe
        src={link}
        ref={iframeRef}
        title="Tutor's slide"
        allowFullScreen={true}
        onLoad={onIframeLoad}
        className='w-full h-full mx-2'
        style={{
          pointerEvents: 'none',
          height: iframeHeight,
        }}
      ></iframe>
    </div>
  );
};

export default HtmlContent;
